import { Button, Col, Form, Row } from 'react-bootstrap';
import { Plus, Trash2 } from 'lucide-react';
import type { QuestionnaireOption, QuestionnaireQuestion } from '../../../infrastructure/QuestionnaireClient';
import { createQuestionId } from '../utils/questionnaireIds';
import { questionTypeUsesOptions } from '../utils/questionTypes';

interface QuestionBuilderProps {
  questions: QuestionnaireQuestion[];
  onChange: (questions: QuestionnaireQuestion[]) => void;
  readOnly: boolean;
}

const questionTypes = ['single_select', 'multi_select', 'rating', 'free_text', 'email'];

export function QuestionBuilder({ questions, onChange, readOnly }: QuestionBuilderProps) {
  const addQuestion = () => {
    onChange([
      ...questions,
      {
        questionId: createQuestionId(),
        type: 'single_select',
        label: '',
        options: [],
      },
    ]);
  };

  const updateQuestion = (index: number, patch: Partial<QuestionnaireQuestion>) => {
    onChange(
      questions.map((question, currentIndex) => (currentIndex === index ? { ...question, ...patch } : question)),
    );
  };

  const updateQuestionType = (index: number, type: string) => {
    const question = questions[index];
    if (!question) return;

    updateQuestion(index, {
      type,
      options: questionTypeUsesOptions(type) ? question.options ?? [] : [],
    });
  };

  const addOption = (index: number) => {
    const question = questions[index];
    if (!question) return;

    const options = question.options ?? [];
    updateQuestion(index, { options: [...options, { value: '', label: '' }] });
  };

  const updateOption = (questionIndex: number, optionIndex: number, patch: Partial<QuestionnaireOption>) => {
    const question = questions[questionIndex];
    if (!question) return;

    const options = question.options ?? [];
    updateQuestion(questionIndex, {
      options: options.map((option, currentIndex) => (currentIndex === optionIndex ? { ...option, ...patch } : option)),
    });
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const question = questions[questionIndex];
    if (!question) return;

    updateQuestion(questionIndex, {
      options: (question.options ?? []).filter((_, currentIndex) => currentIndex !== optionIndex),
    });
  };

  const removeQuestion = (index: number) => {
    onChange(questions.filter((_, currentIndex) => currentIndex !== index));
  };

  return (
    <section className="mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h3 className="h6 mb-0">Quiz Builder</h3>
        <Button variant="outline-primary" size="sm" onClick={addQuestion} disabled={readOnly}>
          <Plus size={15} className="me-1" />
          Add
        </Button>
      </div>
      {questions.map((question, index) => (
        <div className="border rounded p-3 mb-3" key={`${question.questionId}-${index}`}>
          <Row className="g-3">
            <Col md={3}>
              <Form.Label>Generated Quiz ID</Form.Label>
              <div className="font-monospace small text-muted text-break border rounded px-2 py-1 bg-light">
                {question.questionId}
              </div>
            </Col>
            <Col md={3}>
              <Form.Label htmlFor={`question-${index}-type`}>Question type</Form.Label>
              <Form.Select
                id={`question-${index}-type`}
                size="sm"
                value={question.type ?? 'single_select'}
                disabled={readOnly}
                onChange={(event) => updateQuestionType(index, event.target.value)}
              >
                {questionTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </Form.Select>
            </Col>
            <Col md={5}>
              <Form.Label>Label</Form.Label>
              <Form.Control
                size="sm"
                value={question.label ?? ''}
                disabled={readOnly}
                onChange={(event) => updateQuestion(index, { label: event.target.value })}
              />
            </Col>
            {questionTypeUsesOptions(question.type) && (
              <Col md={11}>
                <div className="d-flex align-items-center justify-content-between mb-2">
                  <Form.Label className="mb-0">Options</Form.Label>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    type="button"
                    disabled={readOnly}
                    onClick={() => addOption(index)}
                    aria-label={`Add option to ${question.label || question.questionId}`}
                  >
                    <Plus size={14} className="me-1" />
                    Add option
                  </Button>
                </div>
                <div className="d-grid gap-2">
                  {(question.options ?? []).map((option, optionIndex) => (
                    <Row className="g-2 align-items-end" key={`${question.questionId}-option-${optionIndex}`}>
                      <Col md={5}>
                        <Form.Label htmlFor={`question-${index}-option-${optionIndex}-value`} className="small text-muted">
                          Option {optionIndex + 1} value
                        </Form.Label>
                        <Form.Control
                          id={`question-${index}-option-${optionIndex}-value`}
                          size="sm"
                          value={option.value}
                          disabled={readOnly}
                          onChange={(event) => updateOption(index, optionIndex, { value: event.target.value })}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Label htmlFor={`question-${index}-option-${optionIndex}-label`} className="small text-muted">
                          Option {optionIndex + 1} visible label
                        </Form.Label>
                        <Form.Control
                          id={`question-${index}-option-${optionIndex}-label`}
                          size="sm"
                          value={option.label}
                          disabled={readOnly}
                          onChange={(event) => updateOption(index, optionIndex, { label: event.target.value })}
                        />
                      </Col>
                      <Col md={1} className="d-flex justify-content-end">
                        <Button
                          variant="outline-danger"
                          size="sm"
                          type="button"
                          onClick={() => removeOption(index, optionIndex)}
                          disabled={readOnly}
                          aria-label={`Remove option ${optionIndex + 1} from ${question.label || question.questionId}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </Col>
                    </Row>
                  ))}
                </div>
              </Col>
            )}
            <Col md={1} className="d-flex align-items-end justify-content-end">
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeQuestion(index)}
                disabled={readOnly}
                aria-label={`Remove ${question.questionId}`}
              >
                <Trash2 size={15} />
              </Button>
            </Col>
          </Row>
        </div>
      ))}
    </section>
  );
}
